package com.sweetlime.bingeamigo.presentation.features.settings

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ExitToApp
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Info
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.sweetlime.bingeamigo.presentation.features.auth.AuthViewModel
import com.sweetlime.bingeamigo.presentation.features.profile.ProfileViewModel
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun SettingsContent(
    onBack: () -> Unit,
    onLogout: () -> Unit
) {
    val authViewModel: AuthViewModel = koinInject()
    val profileViewModel: ProfileViewModel = koinViewModel()
    val uriHandler = LocalUriHandler.current

    val isDeletingAccount by profileViewModel.isDeletingAccount.collectAsState()
    val deleteAccountError by profileViewModel.deleteAccountError.collectAsState()
    var showDeleteDialog by remember { mutableStateOf(false) }

    if (showDeleteDialog) {
        AlertDialog(
            onDismissRequest = { if (!isDeletingAccount) showDeleteDialog = false },
            title = { Text("Delete account?", color = Color.White) },
            text = {
                Text(
                    text = "This is permanent and cannot be undone. Your library, watchlist, activity, and all account data will be deleted.",
                    color = Color.White.copy(alpha = 0.8f)
                )
            },
            confirmButton = {
                Button(
                    onClick = { profileViewModel.deleteAccount { onLogout() } },
                    enabled = !isDeletingAccount,
                    colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Error)
                ) {
                    if (isDeletingAccount) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            strokeWidth = 2.dp,
                            color = Color.White
                        )
                    } else {
                        Text("Delete forever", color = Color.White, fontWeight = FontWeight.Bold)
                    }
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showDeleteDialog = false },
                    enabled = !isDeletingAccount
                ) {
                    Text("Cancel", color = Color.White.copy(alpha = 0.7f))
                }
            },
            containerColor = BrandColors.Surface,
            shape = RoundedCornerShape(20.dp)
        )
    }

    deleteAccountError?.let { errorMsg ->
        AlertDialog(
            onDismissRequest = { profileViewModel.clearDeleteAccountError() },
            title = { Text("Could not delete account", color = Color.White) },
            text = { Text(errorMsg, color = Color.White.copy(alpha = 0.8f)) },
            confirmButton = {
                TextButton(onClick = { profileViewModel.clearDeleteAccountError() }) {
                    Text("OK", color = BrandColors.Secondary)
                }
            },
            containerColor = BrandColors.Surface,
            shape = RoundedCornerShape(20.dp)
        )
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(BrandColors.Background)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
        ) {
            Spacer(Modifier.statusBarsPadding())
            Spacer(Modifier.height(72.dp))

            Text(
                text = "Settings",
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = Color.White,
                modifier = Modifier.padding(horizontal = 20.dp)
            )

            Spacer(Modifier.height(32.dp))

            SettingsSection(title = "Account") {
                SettingsRow(
                    icon = Icons.AutoMirrored.Filled.ExitToApp,
                    label = "Logout",
                    onClick = { authViewModel.logout(); onLogout() }
                )
                HorizontalDivider(
                    color = Color.White.copy(alpha = 0.06f),
                    modifier = Modifier.padding(start = 56.dp)
                )
                SettingsRow(
                    icon = Icons.Default.Delete,
                    label = "Delete Account",
                    tint = BrandColors.Error,
                    onClick = { showDeleteDialog = true }
                )
            }

            Spacer(Modifier.height(20.dp))

            SettingsSection(title = "Legal") {
                SettingsRow(
                    icon = Icons.Default.Info,
                    label = "Privacy Policy",
                    onClick = { uriHandler.openUri("https://bingeamigo.com/privacy-policy.html") }
                )
                HorizontalDivider(
                    color = Color.White.copy(alpha = 0.06f),
                    modifier = Modifier.padding(start = 56.dp)
                )
                SettingsRow(
                    icon = Icons.Default.Info,
                    label = "Terms & Conditions",
                    onClick = { uriHandler.openUri("https://bingeamigo.com/terms-and-conditions.html") }
                )
            }

            Spacer(Modifier.height(48.dp))
        }

        IconButton(
            onClick = onBack,
            modifier = Modifier
                .statusBarsPadding()
                .padding(start = 20.dp, top = 12.dp)
                .background(Color.Black.copy(alpha = 0.5f), CircleShape)
                .align(Alignment.TopStart)
        ) {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                contentDescription = "Back",
                tint = Color.White
            )
        }
    }
}

@Composable
private fun SettingsSection(
    title: String,
    content: @Composable () -> Unit
) {
    Column(modifier = Modifier.padding(horizontal = 20.dp)) {
        Text(
            text = title.uppercase(),
            style = MaterialTheme.typography.labelSmall,
            color = BrandColors.TextSecondary,
            fontWeight = FontWeight.Bold,
            modifier = Modifier.padding(start = 4.dp, bottom = 10.dp)
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(18.dp))
                .background(BrandColors.Surface)
        ) {
            content()
        }
    }
}

@Composable
private fun SettingsRow(
    icon: ImageVector,
    label: String,
    tint: Color = Color.White,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = tint.copy(alpha = 0.7f),
            modifier = Modifier.size(22.dp)
        )
        Text(
            text = label,
            color = tint,
            style = MaterialTheme.typography.bodyLarge,
            modifier = Modifier.weight(1f)
        )
        Icon(
            imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
            contentDescription = null,
            tint = Color.White.copy(alpha = 0.25f),
            modifier = Modifier.size(20.dp)
        )
    }
}
